import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usersAPI, deliveriesAPI } from '../services/api';
import './DriverProfile.css';

function DriverProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [driver, setDriver] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [licensePlateUrl, setLicensePlateUrl] = useState(null);

  useEffect(() => {
    loadDriverData();
  }, [id]);

  const loadDriverData = async () => {
    try {
      const driverResponse = await usersAPI.getById(id);
      setDriver(driverResponse.data);
      setFormData(driverResponse.data);

      // Load license plate URL if photo exists (handles encrypted S3 files)
      if (driverResponse.data.license_plate_photo) {
        try {
          const urlResponse = await usersAPI.getLicensePlateUrl(id);
          setLicensePlateUrl(urlResponse.data.url);
        } catch (error) {
          console.log('Could not load license plate URL:', error);
        }
      }

      // Try to load driver's deliveries (if they have any)
      try {
        const deliveriesResponse = await deliveriesAPI.getAll();
        // Filter deliveries for this driver
        const driverDeliveries = deliveriesResponse.data.filter(
          d => d.driver_id === parseInt(id)
        );
        setDeliveries(driverDeliveries);
      } catch (error) {
        console.log('Could not load deliveries:', error);
      }
    } catch (error) {
      console.error('Error loading driver:', error);
      alert('Failed to load driver information');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await usersAPI.update(id, formData);
      alert('Driver information updated successfully!');
      setEditing(false);
      loadDriverData();
    } catch (error) {
      alert('Error updating driver: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleToggleActive = async () => {
    try {
      await usersAPI.toggleActive(id);
      loadDriverData();
    } catch (error) {
      alert('Error updating driver status');
    }
  };

  const handleApprove = async () => {
    if (!window.confirm('Are you sure you want to approve this driver?')) {
      return;
    }

    try {
      await usersAPI.approveDriver(id);
      alert('Driver approved successfully!');
      loadDriverData();
    } catch (error) {
      alert('Error approving driver: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleDeny = async () => {
    const reason = prompt('Enter reason for denial (optional):');
    if (reason === null) {
      return; // User cancelled
    }

    try {
      await usersAPI.denyDriver(id, reason);
      alert('Driver application denied.');
      loadDriverData();
    } catch (error) {
      alert('Error denying driver: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const getLicensePlateUrl = () => {
    // Return the securely fetched URL (presigned for S3 encrypted files)
    return licensePlateUrl;
  };

  if (loading) {
    return <div className="loading">Loading driver information...</div>;
  }

  if (!driver) {
    return (
      <div className="page">
        <p>Driver not found</p>
        <button onClick={() => navigate('/admin/drivers')} className="btn btn-secondary">
          Back to Drivers
        </button>
      </div>
    );
  }

  return (
    <div className="page driver-profile-page">
      <div className="page-header">
        <button onClick={() => navigate('/admin/drivers')} className="btn btn-secondary">
          ← Back to Drivers
        </button>
        <div className="header-actions">
          {driver.approval_status === 'pending' && (
            <>
              <button onClick={handleApprove} className="btn btn-success">
                ✓ Approve Driver
              </button>
              <button onClick={handleDeny} className="btn btn-danger">
                ✗ Deny Application
              </button>
            </>
          )}
          {driver.approval_status === 'approved' && (
            <button
              onClick={handleToggleActive}
              className={`btn ${driver.is_active ? 'btn-warning' : 'btn-success'}`}
            >
              {driver.is_active ? 'Deactivate' : 'Activate'} Driver
            </button>
          )}
          {!editing && (
            <button onClick={() => setEditing(true)} className="btn btn-primary">
              Edit Profile
            </button>
          )}
        </div>
      </div>

      <div className="profile-content">
        {/* Driver Info Card */}
        <div className="card driver-info-card">
          <div className="driver-header">
            <div className="driver-avatar-large">
              {driver.first_name[0]}{driver.last_name[0]}
            </div>
            <div>
              <h1>{driver.first_name} {driver.last_name}</h1>
              <div className="badges">
                <span className={`status-badge ${driver.is_active ? 'active' : 'inactive'}`}>
                  {driver.is_active ? 'Active' : 'Inactive'}
                </span>
                <span className={`approval-badge ${driver.approval_status}`}>
                  {driver.approval_status === 'pending' && '⏳ Pending Approval'}
                  {driver.approval_status === 'approved' && '✓ Approved'}
                  {driver.approval_status === 'denied' && '✗ Denied'}
                </span>
              </div>
            </div>
          </div>

          {editing ? (
            <form onSubmit={handleSubmit} className="edit-form">
              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone || ''}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Vehicle Information</label>
                <input
                  type="text"
                  name="vehicle_info"
                  value={formData.vehicle_info || ''}
                  onChange={handleInputChange}
                  placeholder="e.g., 2020 Ford F-150, White"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>License Number</label>
                <input
                  type="text"
                  name="license_number"
                  value={formData.license_number || ''}
                  onChange={handleInputChange}
                  className="form-input"
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  Save Changes
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditing(false);
                    setFormData(driver);
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <div className="driver-details">
              <div className="detail-row">
                <span className="detail-label">Email:</span>
                <span className="detail-value">{driver.email}</span>
              </div>
              {driver.phone && (
                <div className="detail-row">
                  <span className="detail-label">Phone:</span>
                  <span className="detail-value">{driver.phone}</span>
                </div>
              )}
              {driver.vehicle_info && (
                <div className="detail-row">
                  <span className="detail-label">Vehicle:</span>
                  <span className="detail-value">{driver.vehicle_info}</span>
                </div>
              )}
              {driver.license_number && (
                <div className="detail-row">
                  <span className="detail-label">License Number:</span>
                  <span className="detail-value">{driver.license_number}</span>
                </div>
              )}
              {driver.license_plate_photo && (
                <div className="detail-row">
                  <span className="detail-label">License Plate Photo:</span>
                  <span className="detail-value">
                    {licensePlateUrl ? (
                      <a
                        href={getLicensePlateUrl()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="view-photo-link"
                      >
                        View Photo →
                      </a>
                    ) : (
                      <span>Loading...</span>
                    )}
                  </span>
                </div>
              )}
              <div className="detail-row">
                <span className="detail-label">Work Authorization:</span>
                <span className="detail-value">
                  {driver.can_work_in_us ? (
                    <span className="authorized">✓ Authorized to work in US</span>
                  ) : (
                    <span className="not-authorized">⚠ Not specified</span>
                  )}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Account Created:</span>
                <span className="detail-value">
                  {new Date(driver.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* License Plate Photo Display */}
        {driver.license_plate_photo && licensePlateUrl && (
          <div className="card license-plate-card">
            <h2>License Plate Photo</h2>
            <div className="license-plate-display">
              <img src={getLicensePlateUrl()} alt="License plate" />
            </div>
          </div>
        )}

        {/* Delivery History Card */}
        <div className="card delivery-history-card">
          <h2>Delivery History</h2>
          {deliveries.length === 0 ? (
            <p className="no-data">No deliveries yet</p>
          ) : (
            <div className="deliveries-list">
              {deliveries.slice(0, 10).map((delivery) => (
                <div
                  key={delivery.id}
                  className="delivery-item"
                  onClick={() => navigate(`/deliveries/${delivery.id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <div>
                    <strong>Delivery #{delivery.id}</strong>
                    <p className="delivery-status">{delivery.status}</p>
                  </div>
                  <div className="delivery-date">
                    {new Date(delivery.created_at).toLocaleDateString()}
                  </div>
                </div>
              ))}
              {deliveries.length > 10 && (
                <p className="more-info">
                  Showing 10 of {deliveries.length} deliveries
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DriverProfile;
